import { useState } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta, { useJsonLd } from "@/hooks/usePageMeta";
import { siteMeta } from "@/mocks/articles";

const faqs = [
  {
    q: "你们的内容都是「黑料」吗？会不会造谣或抹黑？",
    a: "不会。栏目虽以「盘点热议」为出发点，但我们坚持「先核实、再盘点」，所有涉及事实的信息都会标注来源与核实状态。未经证实的说法会明确提示「待核实」，绝不把传闻包装成定论，也绝不针对个人进行恶意抹黑。",
  },
  {
    q: "如果我发现某篇文章有错误，该如何提出更正？",
    a: "欢迎通过「读者联络」页面或直接发送邮件到 " + siteMeta.email + " 提交更正线索。请尽量附上可核实的来源与具体指出的段落，编辑部核实后会及时在原文补充更正说明，并在显著位置标注更新状态。",
  },
  {
    q: "你们如何处理涉及个人隐私的内容？",
    a: "我们对隐私问题非常谨慎：涉及非公开地址、联系方式、家庭私事等内容一律回避；涉及未成年人的信息不采集、不传播。分享八卦的前提，是绝不让它伤害到与事件无关的真实的人。",
  },
  {
    q: "文章里的「待核实」是什么意思？",
    a: "当一条信息仅有单方说法、尚无多方交叉印证时，我们会以「待核实」标注，并说明当前掌握的信息范围。它代表编辑部尚未给该说法下定论，读者可据此自行判断可信度，我们也会在事态明朗后持续更新。",
  },
  {
    q: "你们的盘点依据哪些公开信息？",
    a: "主要来源包括公开采访与官方声明、可查证的公开报道、公开的行业资料与社交媒体公开内容等。编辑部会尽量保留原始出处，并在文中对时间线与上下文做必要交代，避免断章取义。",
  },
  {
    q: "可以转载或引用你们的内容吗？",
    a: "欢迎在注明出处「" + siteMeta.brand + "」与原文链接的前提下进行非商业转载。商业合作与深度授权请通过读者联络页面与我们联系，未经授权的大篇幅搬运将被追责。",
  },
  {
    q: "订阅邮件会不会被滥发？退订方便吗？",
    a: "订阅邮件仅用于每周精选盘点，绝不售卖邮箱信息，也不会发送无关广告。每封邮件底部都含一键退订入口，退订后即停止发送，绝无骚扰。",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(0);

  usePageMeta({
    title: "常见问题_每日黑料内容原则与读者疑问解答",
    description:
      "关于每日黑料的内容原则、信息核实、隐私保护、更正机制与订阅退订的常见问题解答。先核实再盘点、不造谣不抹黑，是我们回答每一条疑问的前提。",
    keywords: "每日黑料,常见问题,每日黑料内容原则,每日黑料FAQ,公众人物盘点,信息核实",
    canonicalPath: "/faq",
  });

  useJsonLd([
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "/" },
        { "@type": "ListItem", position: 2, name: "常见问题" },
      ],
    },
  ]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6">
      {/* 面包屑 */}
      <nav className="flex items-center gap-1.5 text-xs text-foreground-600" aria-label="面包屑">
        <Link to="/" className="flex items-center gap-1 hover:text-primary-700">
          <i className="ri-home-5-line"></i>首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-primary-700">常见问题</span>
      </nav>

      <div className="grid grid-cols-1 gap-10 pt-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SectionHeading
            tag="FAQ"
            title="关于内容与读者的常见疑问"
            sub="围绕信息核实、隐私边界、更正机制与订阅退订，编辑部整理了大家问得最多的几个问题。"
            size="lg"
          />
          <div className="mt-6 space-y-3">
            {faqs.map((f, i) => {
              const active = open === i;
              return (
                <div
                  key={f.q}
                  className={`overflow-hidden rounded-lg border transition-colors ${
                    active ? "border-primary-300 bg-background-100/70" : "border-foreground-900/10 bg-background-100/40"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpen(active ? -1 : i)}
                    aria-expanded={active}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="flex items-center gap-3">
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center font-label text-sm ${
                          active ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-900"
                        }`}
                      >
                        Q
                      </span>
                      <span className="text-[15px] font-bold text-foreground-950">{f.q}</span>
                    </span>
                    <i
                      className={`ri-add-line shrink-0 text-xl transition-transform ${
                        active ? "rotate-45 text-primary-600" : "text-foreground-500"
                      }`}
                    ></i>
                  </button>
                  {active && (
                    <div className="border-t border-foreground-900/10 px-5 py-4">
                      <p className="pl-9 text-[15px] leading-7 text-foreground-700">{f.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-lg bg-foreground-950 p-6 text-background-100">
            <i className="ri-customer-service-2-line text-2xl text-accent-400"></i>
            <h3 className="mt-3 font-heading text-xl font-black text-background-50">没找到答案？</h3>
            <p className="mt-2 text-sm leading-6 text-background-200">
              关于内容、纠错或合作，都可以直接和我们聊聊。
            </p>
            <Link
              to="/contact"
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-foreground-950 hover:bg-accent-600"
            >
              去读者联络<i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="rounded-lg border border-foreground-900/10 bg-background-100/60 p-5">
            <h3 className="font-heading text-lg font-black text-foreground-950">快捷问题</h3>
            <ul className="mt-3 space-y-2 text-sm">
              {[
                { label: "如何核实一条信息？", idx: 0 },
                { label: "如何提交更正？", idx: 1 },
                { label: "如何退订邮件？", idx: 6 },
              ].map((item) => (
                <li key={item.label}>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(item.idx);
                      document.getElementById("faq-top")?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="flex items-center gap-2 text-foreground-700 hover:text-primary-700"
                  >
                    <i className="ri-question-line text-primary-600"></i>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
      <div id="faq-top"></div>
    </div>
  );
}